import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import TextArea from "@/common/components/TextArea";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useMaintenanceEstablishmentChoiceAuctionEntryProposalsDisqualify } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/hooks/useMaintenanceEstablishmentChoiceAuctionEntryProposalsDisqualify";
import { DisqualifyProposalValidationSchema, disqualifyProposalSchema } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/schemas/disqualifyProposal.schema";
import { MaintenanceEstablishmentChoiceAuctionEntryUpdatedSummaryProposalData } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/types/updatedSummary.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

interface DisqualifyProposalProps {
  proposal: MaintenanceEstablishmentChoiceAuctionEntryUpdatedSummaryProposalData | null;
  onDismiss: () => void;
}

const DisqualifyProposal = ({
  proposal,
  onDismiss,
}: DisqualifyProposalProps) => {
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const queryClient = useQueryClient();

  const {
    register,
    reset,
    setError,
    handleSubmit,
    formState: {
      errors,
    },
  } = useForm<DisqualifyProposalValidationSchema>({
    resolver: zodResolver(disqualifyProposalSchema),
  });

  const {
    mutate: disqualifyProposal,
    isPending: disqualifyProposalIsPending,
  } = useMaintenanceEstablishmentChoiceAuctionEntryProposalsDisqualify({
    proposalId: proposal?.id,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['maintenanceEstablishmentChoiceAuctionEntryUpdatedSummary'],
        });

        onDismiss();
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({
          setError,
          setMessages,
        })(error);
      }
    },
  });

  const onSubmit: SubmitHandler<DisqualifyProposalValidationSchema> = (data) => disqualifyProposal(data);

  useEffect(() => {
    reset();
  }, [!!proposal]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Modal
        isOpen={!!proposal}
        setIsOpen={(value) => {
          if (!value) {
            onDismiss();
          }
        }}
        title="Desclassificar proposta"
        titleClass="text-2xl"
        useCancellableFooter
        cancelButtonClassName="bg-white-200"
        bodyClassName="text-white-800"
        confirmButtonIsLoading={disqualifyProposalIsPending}
        onConfirm={handleSubmit(onSubmit)}
        body={(
          <div className="flex flex-col gap-3">
            <p className="text-base font-bold">
              Comente o motivo para desclassificação da proposta
            </p>

            <TextArea
              label="Justificativa para desclassificação"
              markAsRequired
              placeholder="Escreva aqui sua justificativa"
              {...register('reason')}
              errorMessage={errors?.reason?.message}
            />
          </div>
        )}
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  );
}

export default DisqualifyProposal;
